import { Box, Typography, Chip, IconButton } from '@mui/material';
import { ChevronRight, ExpandMore } from '@mui/icons-material';
import { getDomainIcon } from './domain-icons';

export interface DomainNodeData {
  domainId: string;
  name: string;
  description: string;
  level: string;
  billingTier: string;
  status: string;
  entities?: string[];
  children?: DomainNodeData[];
}

export interface DomainTreeNodeProps {
  domain: DomainNodeData;
  depth: number;
  selected?: string;
  onSelect?: (domainId: string) => void;
  expanded: Set<string>;
  onToggle: (domainId: string) => void;
}

const statusColor = (status: string): 'success' | 'warning' | 'default' | 'error' => {
  switch (status) {
    case 'Active': return 'success';
    case 'Deprecated': return 'warning';
    case 'Removed': return 'default';
    default: return 'default';
  }
};

const tierColor = (tier: string): 'primary' | 'secondary' | 'default' => {
  switch (tier) {
    case 'premium': return 'secondary';
    case 'enterprise': return 'secondary';
    case 'standard': return 'primary';
    default: return 'default';
  }
};

export function DomainTreeNode({
  domain,
  depth,
  selected,
  onSelect,
  expanded,
  onToggle,
}: DomainTreeNodeProps) {
  const hasChildren = domain.children && domain.children.length > 0;
  const isExpanded = expanded.has(domain.domainId);
  const isSelected = selected === domain.domainId;
  const icon = getDomainIcon(domain.domainId);

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          py: 0.5,
          px: 1,
          pl: depth * 2 + 1,
          cursor: onSelect ? 'pointer' : 'default',
          borderRadius: 1,
          bgcolor: isSelected ? 'action.selected' : 'transparent',
          '&:hover': onSelect ? { bgcolor: 'action.hover' } : {},
        }}
        onClick={() => onSelect?.(domain.domainId)}
      >
        {hasChildren ? (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onToggle(domain.domainId);
            }}
            sx={{ p: 0.25 }}
          >
            {isExpanded ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />}
          </IconButton>
        ) : (
          <Box sx={{ width: 28 }} />
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>{icon}</Box>

        <Typography variant="body2" sx={{ fontWeight: isSelected ? 600 : 400, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {domain.name}
        </Typography>

        <Chip label={domain.status} color={statusColor(domain.status)} size="small" sx={{ height: 20, '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' } }} />

        <Chip label={domain.billingTier} color={tierColor(domain.billingTier)} size="small" variant="outlined" sx={{ height: 20, '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' } }} />
      </Box>

      {isExpanded && hasChildren && (
        <Box>
          {domain.children!.map((child) => (
            <DomainTreeNode
              key={child.domainId}
              domain={child}
              depth={depth + 1}
              selected={selected}
              onSelect={onSelect}
              expanded={expanded}
              onToggle={onToggle}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}
